import { AfterViewInit, ChangeDetectionStrategy, Component, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { Observable, of } from "rxjs";
import { filter, finalize, take } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";
import { getEnumList } from "src/app/_helpers/enum-helper";
import { RiskAssessmentReportPopupComponent } from "src/app/common-components/risk-assessment-report-popup/risk-assessment-report-popup.component";
import { RiskTreatmentReportComponent } from "src/app/common-components/risk-treatment-report/risk-treatment-report.component";
import { AssessmentStatusEnum } from "src/app/enums/assessmentStatus.enum";
import { PermissionFlags } from "src/app/enums/permissionFlags.enum";
import { QuantitativeDecisionStatisticNamesEnum } from "src/app/enums/quantitativeDecisionStatistic.enum";
import { RiskRegisterGenerationStatus } from "src/app/enums/riskRegisterGenerationStatus.enum";
import { RiskAssessmentDialogComponent } from "src/app/modals/risk-assessment-dialog/risk-assessment-dialog.component";
import { RiskAssessment } from "src/app/models/risk-assessment.model";
import { AuthService } from "src/app/services/auth.service";
import { CommonService } from "src/app/services/common.service";
import { GridViewService } from "src/app/services/grid-view.service";
import { RiskAssessmentService } from "src/app/services/risk-assessment.service";
import { SegProfileService } from "src/app/services/seg-profile.service";
import { ToastService } from "src/app/services/toast.service";
import { ConfirmDialogComponent } from "src/app/shared/confirmDialog/confirmDialog.component";
import { riskAssessmentRouteConfig } from "src/app/shared/constants/riskAssessmentRouteConfig.constants";
import { RolePermissionObjects } from "src/app/shared/constants/rolePermissionObjects.constants";
import { DeleteDialog } from "src/app/shared/delete-dialog/delete-dialog.component";
import nameOf from "src/app/shared/form/nameOf.helper";
import { ChildColumnConfig, ColumnConfig } from "src/app/shared/grid/core-grid/columnConfig.model";
import { CoreGridComponent, ExportMenuOption } from "src/app/shared/grid/core-grid/core-grid.component";
import { GridSettingsService } from "src/app/shared/grid/core-grid/grid-settings-flyout/grid-settings.service";
import { escapeHtml } from "src/app/shared/helpers/html-escape.helper";
import { DateFormatPipe } from "src/app/shared/pipes/date-format.pipe";

@Component({
    standalone: false,
    selector: "app-risk-assessment",
    templateUrl: "./../../shared/grid/core-grid/core-grid.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./../../shared/grid/core-grid/core-grid.component.scss", "./risk-assessment.component.scss"],
    providers: [DateFormatPipe],
})
export class RiskAssessmentComponent
    extends CoreGridComponent<RiskAssessment>
    implements OnInit, AfterViewInit
{
    private static readonly GENERATION_BADGE_META: Partial<Record<RiskRegisterGenerationStatus, { cssClass: string; label: string }>> = {
        [RiskRegisterGenerationStatus.Queued]: { cssClass: "generation-badge-pending", label: "Generating…" },
        [RiskRegisterGenerationStatus.Running]: { cssClass: "generation-badge-pending", label: "Generating…" },
        [RiskRegisterGenerationStatus.Failed]: { cssClass: "generation-badge-failed", label: "Generation failed" },
    };

    private static readonly STATUS_PILL_META: Record<AssessmentStatusEnum, { cssClass: string; label: string }> = {
        [AssessmentStatusEnum.Draft]: { cssClass: "risk-drafted", label: "Draft" },
        [AssessmentStatusEnum.InReview]: { cssClass: "risk-in-review", label: "In Review" },
        [AssessmentStatusEnum.Approved]: { cssClass: "risk-approved", label: "Approved" },
        [AssessmentStatusEnum.Rejected]: { cssClass: "risk-rejected", label: "Rejected" },
    };

    settingCurrentId: string | null = null;
    canUnapprove = false;
    private riskAssessmentReportOption: ExportMenuOption;
    private riskTreatmentReportOption: ExportMenuOption;

    constructor(
        private riskAssessmentService: RiskAssessmentService,
        private segProfileService: SegProfileService,
        toastService: ToastService,
        dialog: MatDialog,
        commonService: CommonService,
        authService: AuthService,
        gridViewService: GridViewService,
        gridSettingsService: GridSettingsService,
        private dateFormatPipe: DateFormatPipe,
        private router: Router,
        private translateService: TranslateService,
    ) {
        super(toastService, dialog, commonService, authService, gridViewService, gridSettingsService);
        this.gridService = riskAssessmentService;
    }

    protected get objectId(): string {
        return RolePermissionObjects.RiskAssessment;
    }

    protected override get isGridViewEnabled(): boolean {
        return true;
    }

    protected override get enableCheckboxSelection(): boolean {
        return true;
    }

    get hasOrgFilter(): boolean {
        return true;
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.initUnapprovePermission();
    }

    ngAfterViewInit(): void {
        super.ngAfterViewInit();
        this.loadHeaderData();
    }

    initUnapprovePermission(): void {
        this.permissionService.isLoaded$
            .pipe(
                filter(Boolean),
                take(1),
            )
            .subscribe(() => {
                this.canUnapprove =
                    this.permissionService.hasPermissionFlag(
                        RolePermissionObjects.RiskAssessment,
                        PermissionFlags.UpdateHierarchy,
                    );
            });
    }

    override initializeTabs() {
        return riskAssessmentRouteConfig;
    }

    override initializeOverviewConfigs(): void {
        this.overviewConfigs = [];
    }

    override getOverviewData(): Observable<void> {
        return of(undefined);
    }

    override initializeProperties(): void {
        this.addEntityTitle = "Risk Assessment";
        this.searchPlaceholder = "Search Risk Assessment";
        this.riskAssessmentReportOption = {
            label: "Risk Assessment",
            disabled: true,
            onClick: () => this.openRiskAssessmentReportDialog(),
        };
        this.riskTreatmentReportOption = {
            label: "Risk Treatment",
            disabled: true,
            onClick: () => this.openRiskTreatmentReportDialog(),
        };
        this.extraExportOptions = [this.riskAssessmentReportOption, this.riskTreatmentReportOption];
    }

    override initializeColumns(): void {
        this.additionalActions = [
            {
                name: "Clone",
                icon: "fa fa-clone",
                action: (row: RiskAssessment) => this.openCloneAssessmentDialog(row),
                hidden: () => !this.canCreate,
            },
            {
                name: "Set as current",
                icon: "far fa-star",
                action: (row: RiskAssessment) => this.setCurrentRiskAssessment(row),
                hidden: (row: RiskAssessment) => !this.canUpdate || !!row.current,
                disabled: () => this.settingCurrentId != null,
            },
            {
                name: "Unset as current",
                icon: "fas fa-star",
                action: (row: RiskAssessment) => this.unsetCurrentRiskAssessment(row),
                hidden: (row: RiskAssessment) => !this.canUpdate || !row.current,
                disabled: () => this.settingCurrentId != null,
            },
        ];

        this.columnConfigs = [
            new ColumnConfig<RiskAssessment>({
                name: nameOf<RiskAssessment>((x) => x.name),
                displayName: "Risk Assessment",
                cellTemplate: (row) => this.buildNameCell(row),
                onClick: (row) => this.goToRiskAssessmentDetails(row.id),
            }),
            new ColumnConfig<RiskAssessment>({
                name: nameOf<RiskAssessment>((x) => x.assessmentStatus),
                displayName: "Status",
                cellTemplate: (row) => this.buildStatusPill(row),
                enumFilterMap: RiskAssessmentComponent.statusFilterMap(),
            }),
            new ColumnConfig<RiskAssessment>({
                name: nameOf<RiskAssessment>((x) => x.periodStart),
                displayName: "Time Period",
                cellTemplate: (row) => `${escapeHtml(this.dateFormatPipe.transform(row.periodStart))} - ${escapeHtml(this.dateFormatPipe.transform(row.periodEnd))}`,
            }),
            new ColumnConfig<RiskAssessment>({
                name: nameOf<RiskAssessment>((x) => x.createdDate),
                displayName: "Created By",
                cellTemplate: (row) => this.buildCreatedByCell(row),
            }),
            new ColumnConfig<RiskAssessment>({
                name: nameOf<RiskAssessment>((x) => x.approvedBy),
                displayName: "Approved By",
                cellTemplate: () => "",
                children: (row) => this.buildApprovedByChildren(row),
                childrenInline: (row) => this.isPendingApproval(row),
            }),
            new ColumnConfig<RiskAssessment>({
                name: nameOf<RiskAssessment>((x) => x.periodEnd),
                displayName: "Period End",
                cellTemplate: (row) => escapeHtml(this.dateFormatPipe.transform(row.periodEnd)),
            }),
            new ColumnConfig<RiskAssessment>({
                name: nameOf<RiskAssessment>((x) => x.approvedDate),
                displayName: "Approved Date",
                cellTemplate: (row) => row.approvedDate ? escapeHtml(this.dateFormatPipe.transform(row.approvedDate)) : "-",
            }),
            new ColumnConfig<RiskAssessment>({
                name: nameOf<RiskAssessment>((x) => x.current),
                displayName: "Current",
                cellTemplate: (row) => row.current ? this.translateService.instant("Yes") : this.translateService.instant("No"),
            }),
            new ColumnConfig<RiskAssessment>({
                name: nameOf<RiskAssessment>((x) => x.quantitativeDecisionStatistic),
                displayName: "Decision Statistic",
                cellTemplate: (row) => this.translateService.instant(QuantitativeDecisionStatisticNamesEnum[row.quantitativeDecisionStatistic] ?? "-"),
                enumFilterMap: RiskAssessmentComponent.decisionStatisticFilterMap(),
            }),
            new ColumnConfig<RiskAssessment>({
                name: nameOf<RiskAssessment>((x) => x.applyShiftCorrection),
                displayName: "Apply Shift Correction",
                cellTemplate: (row) => row.applyShiftCorrection ? this.translateService.instant("Yes") : this.translateService.instant("No"),
            }),
            { name: "action", displayName: "Actions" },
        ];
    }

    protected override onSelectionChanged(): void {
        const hasSelection = (this.selectionService?.selectedItems.length ?? 0) > 0;
        this.riskAssessmentReportOption.disabled = !hasSelection;
        this.riskTreatmentReportOption.disabled = !hasSelection;
    }

    private get selectedAssessmentIds(): string[] {
        return (this.selectionService?.selectedItems ?? []).map((item) => item.id as string);
    }

    goToRiskAssessmentDetails(id: string): void {
        this.router.navigate(["dashboard/risk-assessment", id]);
    }

    override openEntityDialog(entityId?: string): void {
        const dialogRef = this.dialog.open(RiskAssessmentDialogComponent, {
            data: {
                id: entityId,
                isReadonly: false,
                columnDetails: this.columnDetails,
            },
            width: "820px",
            disableClose: true,
            panelClass: "risk-assessment-dialog-panel",
        });

        this.handleDialogClose(dialogRef);
    }

    openCloneAssessmentDialog(riskAssessment: RiskAssessment): void {
        const dialogRef = this.dialog.open(RiskAssessmentDialogComponent, {
            data: {
                cloneFrom: riskAssessment,
                columnDetails: this.columnDetails,
            },
            width: "820px",
            disableClose: true,
            panelClass: "risk-assessment-dialog-panel",
        });

        this.handleDialogClose(dialogRef);
    }

    override openEntityDeleteDialog(entity: RiskAssessment): void {
        this.openDeleteDialog(
            DeleteDialog,
            entity,
            this.riskAssessmentService,
            "Risk Assessment",
        );
    }

    unapproveAssessment(riskAssessment: RiskAssessment): void {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: this.translateService.instant("Un-approve"),
                message: this.translateService.instant(
                    "Un-approving this assessment will re-open it and all its registers for editing. Do you want to continue?",
                ),
                primaryButtonText: this.translateService.instant("Yes"),
                secondaryButtonText: this.translateService.instant("Cancel"),
            },
        });

        dialogRef.afterClosed().subscribe((confirmed: boolean) => {
            if (!confirmed) return;
            this.updateAssessmentStatus(
                riskAssessment,
                AssessmentStatusEnum.InReview,
            );
        });
    }

    updateAssessmentStatus(riskAssessment: RiskAssessment, value: AssessmentStatusEnum): void {
        this.riskAssessmentService
            .update({ ...riskAssessment, assessmentStatus: value })
            .subscribe({
                next: () => this.loadPage(),
                error: (err) =>
                    this.toastService.showError(
                        err,
                        this.translateService.instant(
                            err?.status === 403
                                ? "You do not have permission to un-approve this assessment"
                                : "Failed to update assessment status",
                        ),
                    ),
            });
    }

    openRiskAssessmentReportDialog(): void {
        this.dialog.open(RiskAssessmentReportPopupComponent, {
            data: {
                assessmentIds: this.selectedAssessmentIds,
            },
            panelClass: "risk-assessment-report-popup",
        });
    }

    openRiskTreatmentReportDialog(): void {
        this.dialog.open(RiskTreatmentReportComponent, {
            data: {
                assessmentIds: this.selectedAssessmentIds,
            },
            panelClass: "risk-treatment-report-popup",
        });
    }

    setCurrentRiskAssessment(riskAssessment: RiskAssessment): void {
        this.runCurrentChange(
            riskAssessment.id,
            () => this.segProfileService.setCurrent(riskAssessment.id),
            "Failed to set current Risk Assessment",
        );
    }

    unsetCurrentRiskAssessment(riskAssessment: RiskAssessment): void {
        if (this.settingCurrentId != null) return;
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: this.translateService.instant("Unset as current"),
                message: this.translateService.instant(
                    "This Risk Assessment will no longer be current. Its location will have no current Risk Assessment until you set another one.",
                ),
                primaryButtonText: this.translateService.instant("Yes"),
                secondaryButtonText: this.translateService.instant("Cancel"),
            },
        });

        this.subscriptions.push(
            dialogRef
                .afterClosed()
                .pipe(filter((confirmed: boolean) => !!confirmed))
                .subscribe(() =>
                    this.runCurrentChange(
                        riskAssessment.id,
                        () => this.segProfileService.unsetCurrent(riskAssessment.id),
                        "Failed to unset current Risk Assessment",
                    ),
                ),
        );
    }

    private runCurrentChange(id: string, request: () => Observable<boolean>, errorMessage: string): void {
        if (this.settingCurrentId != null) return;
        this.settingCurrentId = id;
        this.subscriptions.push(
            request()
                .pipe(
                    take(1),
                    finalize(() => (this.settingCurrentId = null)),
                )
                .subscribe({
                    next: () => this.loadPage(),
                    error: (err) => this.toastService.showError(err, this.translateService.instant(errorMessage)),
                }),
        );
    }

    private static statusFilterMap(): { value: number; name: string }[] {
        return getEnumList(AssessmentStatusEnum).map((key) => {
            const value = AssessmentStatusEnum[key as keyof typeof AssessmentStatusEnum];
            return { value, name: RiskAssessmentComponent.STATUS_PILL_META[value].label };
        });
    }

    private static decisionStatisticFilterMap(): { value: number; name: string }[] {
        return getEnumList(QuantitativeDecisionStatisticNamesEnum).map((name) => ({
            value: QuantitativeDecisionStatisticNamesEnum[name as keyof typeof QuantitativeDecisionStatisticNamesEnum],
            name,
        }));
    }

    private buildNameCell(row: RiskAssessment): string {
        const name = escapeHtml(row.name);
        const badge = row.current ? `<span class="current-badge">${this.translateService.instant("Current")}</span>` : "";
        return `${name}${badge}${this.buildGenerationBadge(row)}`;
    }

    private buildGenerationBadge(row: RiskAssessment): string {
        const meta = RiskAssessmentComponent.GENERATION_BADGE_META[row.generationStatus];
        if (!meta) return "";
        return `<span class="generation-badge ${meta.cssClass}">${this.translateService.instant(meta.label)}</span>`;
    }

    private buildStatusPill(row: RiskAssessment): string {
        const meta = RiskAssessmentComponent.STATUS_PILL_META[row.assessmentStatus] ?? { cssClass: "", label: "-" };
        return `<span class="risk-status-btn ${meta.cssClass}"><i>${this.translateService.instant(meta.label)}</i></span>`;
    }

    private buildCreatedByCell(row: RiskAssessment): string {
        const name = escapeHtml(row.createdByName ?? "-");
        const dateLine = row.createdDate
            ? `<span class="light-grey-text">(${escapeHtml(this.dateFormatPipe.transform(row.createdDate))})</span>`
            : "";
        return `<div class="d-flex flex-column justify-content-center"><span class="approved-name mr-1">${name}</span>${dateLine}</div>`;
    }

    private isPendingApproval(row: RiskAssessment): boolean {
        return row.assessmentStatus === AssessmentStatusEnum.Draft
            || row.assessmentStatus === AssessmentStatusEnum.InReview;
    }

    private buildApprovedByChildren(row: RiskAssessment): ChildColumnConfig<RiskAssessment>[] {
        const isDraft = row.assessmentStatus === AssessmentStatusEnum.Draft;

        if (this.isPendingApproval(row)) {
            return [
                new ChildColumnConfig<RiskAssessment>({
                    cssClass: () => `approve-btn transparent-btn bordered-btn${isDraft ? " disabled" : ""}`,
                    cellTemplate: () => this.translateService.instant("Approve"),
                    onClick: (r) => {
                        if (isDraft) return;
                        this.updateAssessmentStatus(r, AssessmentStatusEnum.Approved);
                    },
                }),
                new ChildColumnConfig<RiskAssessment>({
                    cssClass: () => `reject-btn transparent-btn bordered-btn ml-1${isDraft ? " disabled" : ""}`,
                    cellTemplate: () => this.translateService.instant("Reject"),
                    onClick: (r) => {
                        if (isDraft) return;
                        this.updateAssessmentStatus(r, AssessmentStatusEnum.Rejected);
                    },
                }),
            ];
        }

        const children: ChildColumnConfig<RiskAssessment>[] = [
            new ChildColumnConfig<RiskAssessment>({
                cssClass: () => "d-flex flex-column justify-content-center",
                cellTemplate: () => this.buildApprovedByText(row),
            }),
        ];

        if (this.canUnapprove && row.assessmentStatus === AssessmentStatusEnum.Approved) {
            children.push(
                new ChildColumnConfig<RiskAssessment>({
                    cssClass: () => "unapprove-btn transparent-btn bordered-btn mt-1",
                    cellTemplate: () => this.translateService.instant("Un-approve"),
                    tooltip: () => this.translateService.instant("Un-approve the assessment to edit its registers"),
                    onClick: (r) => this.unapproveAssessment(r),
                }),
            );
        }

        return children;
    }

    private buildApprovedByText(row: RiskAssessment): string {
        const name = escapeHtml(row.approvedByName ?? "-");
        const dateLine = row.approvedDate
            ? `<span class="light-grey-text">(${escapeHtml(this.dateFormatPipe.transform(row.approvedDate))})</span>`
            : "";
        return `<div class="d-flex flex-column justify-content-center"><span class="approved-name mr-1">${name}</span>${dateLine}</div>`;
    }
}
